/**
 * The guide, as data.
 *
 * Structured blocks rather than markdown: the shapes a page here needs are
 * few and fixed - prose, a table, a numbered walkthrough with screenshots, a
 * callout - and a renderer for those is less code than a markdown pipeline
 * plus the plugins it would take to get callouts and figures out of it.
 *
 * Screenshots under `public/docs/` are captured from the running app rather
 * than drawn, so a screen that changes shows up here as a stale picture
 * rather than as a diagram that was never quite true to begin with.
 */

export interface Shot {
  /** Path under public/. */
  src: string;
  /** Shown above the image, and what the expand control announces. */
  caption: string;
  alt: string;
}

export type Block =
  | { kind: "p"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "table"; headers: string[]; rows: string[][] }
  | {
      kind: "callout";
      tone: "info" | "warning" | "success";
      title?: string;
      text: string;
    }
  | { kind: "code"; text: string }
  | { kind: "screenshot"; shot: Shot }
  | { kind: "walkthrough"; items: { text: string; shot?: Shot }[] };

export interface GuidePage {
  id: string;
  /** Its address. Every page here is a link someone can send. */
  path: string;
  label: string;
  title: string;
  summary: string;
  body: Block[];
}

export interface GuideSection {
  id: string;
  /** Omitted for a section whose pages need no heading over them. */
  label?: string;
  pages: GuidePage[];
}

export const SECTIONS: GuideSection[] = [
  {
    id: "introduction",
    label: "Introduction",
    pages: [
      {
        id: "overview",
        path: "/docs/overview",
        label: "Overview",
        title: "Lex to CX Designer",
        summary:
          "What this tool does to an Amazon Lex V2 bot, and what you get back.",
        body: [
          {
            kind: "p",
            text:
              "This tool reads an Amazon Lex V2 bot and rebuilds it as an " +
              "Amazon Connect CX Designer application: one flow per intent, a " +
              "Welcome flow that routes between them, the slot types those " +
              "flows need, and a Connect contact flow pointed at the deployed " +
              "result. Four clicks, about a minute, nothing to configure.",
          },
          {
            kind: "p",
            text:
              "It works from the Lex design only: intents, slots, prompts and " +
              "sample utterances. If an intent relied on a Lambda code hook, " +
              "that Lambda is not read and its behaviour is not reproduced.",
          },
          { kind: "h2", text: "What a run produces" },
          {
            kind: "table",
            headers: ["Resource", "What you get"],
            rows: [
              [
                "Application",
                "One CX Designer application, built and deployed to production.",
              ],
              [
                "Flows",
                "One per Lex intent, plus a Welcome flow that routes to them.",
              ],
              [
                "Slot types",
                "Only where the bot needs a real menu of values. Often none.",
              ],
              [
                "Contact flow",
                "An Amazon Connect contact flow wired to the deployment.",
              ],
              [
                "Flow definitions",
                "Every flow as JSON, downloadable from the result page.",
              ],
            ],
          },
          { kind: "h2", text: "The four steps" },
          {
            kind: "table",
            headers: ["Step", "What you do"],
            rows: [
              ["1. Choose an account", "Pick the account whose bots you want."],
              ["2. Choose a Lex bot", "Pick the bot, and press Next."],
              ["3. Convert", "Press one button and watch six stages run."],
              [
                "4. Review the result",
                "Open it in Studio, or download the flows.",
              ],
            ],
          },
          {
            kind: "callout",
            tone: "info",
            title: "A run takes about a minute",
            text:
              "Forty to sixty seconds, depending on how many intents the bot " +
              "has. The page follows it stage by stage, so you can see where " +
              "it is rather than wonder whether it has stalled.",
          },
        ],
      },
    ],
  },

  {
    // No label: the steps are the spine of the guide and do not need a
    // heading announcing that they are steps. A section without one renders
    // its pages directly, and stays out of the breadcrumb.
    id: "steps",
    pages: [
      {
        id: "choose-account",
        path: "/docs/choose-account",
        label: "Step 1: Choose an account",
        title: "Step 1: Choose an account",
        summary: "Pick which account's bots you want to see.",
        body: [
          {
            kind: "p",
            text:
              "The first screen lists the accounts available to you. " +
              "Selecting one is what loads its Lex bots - nothing is fetched " +
              "until you have said which account you mean.",
          },
          {
            kind: "walkthrough",
            items: [
              {
                text: "Open the tool. The account list is the first screen.",
                shot: {
                  src: "/docs/01-accounts.png",
                  caption: "The account list, with NLX-Dev as the only card",
                  alt: "The Choose an account screen showing one account card",
                },
              },
              {
                text:
                  "Select an account. Its Lex bots load immediately, and the " +
                  "bot list replaces this screen.",
              },
            ],
          },
          {
            kind: "callout",
            tone: "info",
            text:
              "There is one account today. The back button on the bot list " +
              "carries its name, so you can always see which account you are " +
              "working in.",
          },
        ],
      },

      {
        id: "choose-bot",
        path: "/docs/choose-bot",
        label: "Step 2: Choose a Lex bot",
        title: "Step 2: Choose a Lex bot",
        summary: "Find the bot, check its locale, and move on.",
        body: [
          {
            kind: "p",
            text:
              "Each card carries the bot's name, its description, its ten " +
              "character bot id, and the locales its DRAFT version has.",
          },
          {
            kind: "walkthrough",
            items: [
              {
                text:
                  "Find the bot. A filter box appears once there are more " +
                  "than six, matching on name, id or description.",
                shot: {
                  src: "/docs/02-bots.png",
                  caption: "The bot list, one card per Lex bot",
                  alt: "The Choose a Lex bot screen showing two bot cards",
                },
              },
              {
                text:
                  "Select it. The card is ticked and Next becomes available.",
                shot: {
                  src: "/docs/03-bot-selected.png",
                  caption: "A selected bot, ticked, with Next enabled",
                  alt: "The bot list with one card selected and a tick shown",
                },
              },
              {
                text:
                  "Press Next. This starts nothing - it opens the Convert " +
                  "screen, where you confirm before anything is created.",
              },
            ],
          },
          {
            kind: "callout",
            tone: "info",
            title: "Reload rather than refresh",
            text:
              "The reload icon re-reads the list from Lex. Use it if a bot you " +
              "expect is missing, rather than refreshing the whole page.",
          },
        ],
      },

      {
        id: "convert",
        path: "/docs/convert",
        label: "Step 3: Convert",
        title: "Step 3: Convert",
        summary: "One button, six stages, about a minute.",
        body: [
          {
            kind: "p",
            text:
              "The Convert screen shows the bot you chose and nothing to " +
              "configure. Converting creates the application, builds it, " +
              "deploys it to production and writes the Connect contact flow - " +
              "a migration that stopped at 'created' would leave drafts nobody " +
              "can call.",
          },
          {
            kind: "screenshot",
            shot: {
              src: "/docs/04-convert.png",
              caption: "The Convert screen, before anything has been created",
              alt: "The Convert screen showing the selected bot and the Convert button",
            },
          },
          { kind: "h2", text: "The six stages" },
          {
            kind: "table",
            headers: ["Stage", "What happens"],
            rows: [
              [
                "Read Lex bot",
                "Exports the intents, slots, prompts and utterances.",
              ],
              [
                "Snapshot workspace",
                "Reads what the CX Designer workspace already has.",
              ],
              [
                "Plan flows",
                "Decides the flows, the slot types, and the copy each agent uses.",
              ],
              [
                "Create in CX Designer",
                "Creates the slot types, the flows, then the application.",
              ],
              [
                "Build & deploy",
                "Builds the application and deploys it to production.",
              ],
              [
                "Contact flow",
                "Writes the Connect contact flow and points it at the deployment.",
              ],
            ],
          },
          {
            kind: "p",
            text:
              "Each stage ticks as it finishes and the next begins spinning. A " +
              "stage that was not needed is marked skipped rather than left " +
              "blank, so a finished run never looks like it stopped short.",
          },
          {
            kind: "screenshot",
            shot: {
              src: "/docs/05-converting.png",
              caption: "A run in progress, with finished stages ticked",
              alt: "The stage list mid-run, some stages ticked and one spinning",
            },
          },
          {
            kind: "callout",
            tone: "info",
            title: "The run cannot be abandoned half way",
            text:
              "Choose another is disabled until the run finishes, so a " +
              "migration that is creating real resources cannot be left " +
              "part-done by accident.",
          },
          {
            kind: "p",
            text: "When every stage has finished, View result opens the summary.",
          },
        ],
      },

      {
        id: "result",
        path: "/docs/result",
        label: "Step 4: Review the result",
        title: "Step 4: Review the result",
        summary: "What was created, where to open it, and how to export it.",
        body: [
          {
            kind: "p",
            text:
              "The result page lists exactly what the run produced. The banner " +
              "at the top is the one-line version; the rows beneath it are the " +
              "detail.",
          },
          {
            kind: "screenshot",
            shot: {
              src: "/docs/06-result.png",
              caption: "The result page after a successful migration",
              alt: "The result page showing the application, flows, contact flow and downloads",
            },
          },
          { kind: "h2", text: "What each row means" },
          {
            kind: "table",
            headers: ["Row", "What it tells you"],
            rows: [
              [
                "Application",
                "The id of the CX Designer application that was created.",
              ],
              [
                "Flows",
                "Every flow the application has, including any that already existed.",
              ],
              [
                "Slot types",
                "Custom slot types created. Absent when the bot needed none.",
              ],
              [
                "Contact flow",
                "The Connect flow, and whether it was created or updated.",
              ],
              [
                "Flow definitions",
                "Each flow as the service stored it, as JSON.",
              ],
            ],
          },
          { kind: "h2", text: "Opening and exporting" },
          {
            kind: "ul",
            items: [
              "Open in CX Designer opens the application in Studio, where you can test it in the chat widget before pointing a phone number at it. It appears when the server has a Studio address configured.",
              "JSON downloads one flow definition. Download all downloads every flow in a single file.",
              "Migrate another Lex bot returns to the bot list and clears the run, so the next bot starts from a blank set of stages.",
            ],
          },
          {
            kind: "callout",
            tone: "info",
            title: "You have to be signed in for the link to work",
            text:
              "Open in CX Designer opens Amazon Connect Customer in a new " +
              "tab. Sign in there as a user with access to the CX Designer " +
              "workspace first - without it the link lands on a sign-in or " +
              "access-denied page rather than on the application.",
          },
          {
            kind: "callout",
            tone: "success",
            title: "The application is already live",
            text:
              "It has been built and deployed to production by the time you " +
              "see this page, and the contact flow points at that deployment. " +
              "The only thing left is to attach a phone number to it.",
          },
        ],
      },
    ],
  },

  {
    id: "reference",
    label: "Reference",
    pages: [
      {
        id: "configuration",
        path: "/docs/configuration",
        label: "Configuration",
        title: "Configuration",
        summary:
          "What the server needs, and how it tells you something is missing.",
        body: [
          {
            kind: "p",
            text:
              "Everything the tool needs is configured on the server: the CX " +
              "Designer workspace and API key, the artifact bucket, and the " +
              "Connect instance. There is nothing to set up in the browser.",
          },
          {
            kind: "p",
            text:
              "When the configuration is sound the header says nothing at all. " +
              "A header that always reports success is one people stop " +
              "reading, so the icon appearing is itself the signal.",
          },
          {
            kind: "screenshot",
            shot: {
              src: "/docs/07-config-warning.png",
              caption: "The configuration warning, opened from the header icon",
              alt: "A panel listing three configuration issues with the setting that fixes each",
            },
          },
          {
            kind: "table",
            headers: ["Icon", "Meaning"],
            rows: [
              ["Nothing", "Everything the tool needs is configured."],
              ["Amber", "A warning. Migrations still run."],
              ["Red", "A blocker. Something will fail until it is fixed."],
            ],
          },
          {
            kind: "p",
            text:
              "Each entry names the setting and what it breaks, so the fix is " +
              "one environment variable rather than a hunt.",
          },
        ],
      },

      {
        id: "troubleshooting",
        path: "/docs/troubleshooting",
        label: "Troubleshooting",
        title: "Troubleshooting",
        summary: "The things that actually go wrong.",
        body: [
          { kind: "h3", text: "A bot is missing from the list" },
          {
            kind: "p",
            text:
              "The list is filtered, and a bot whose tags cannot be read is " +
              "left out rather than shown - so a permissions problem looks the " +
              "same as a bot that was never meant to appear. Press the reload " +
              "icon first; it re-reads the list from Lex.",
          },
          { kind: "h3", text: "A warning icon in the header" },
          {
            kind: "p",
            text:
              "Something the server needs is not configured. Click the icon: " +
              "each entry names the setting and what it blocks. See the " +
              "Configuration page.",
          },
          { kind: "h3", text: "The result lists fewer flows than expected" },
          {
            kind: "p",
            text:
              "Check whether the bot has been migrated before. Flow ids are " +
              "unique across the whole workspace, so a second run finds its " +
              "flows already there and reuses them. They are still listed and " +
              "still attached to the application - but they are not rebuilt.",
          },
          {
            kind: "callout",
            tone: "warning",
            title: "Reused means unchanged",
            text:
              "A flow left from an earlier run keeps its old content, so it " +
              "will not pick up anything the tool has improved since. Delete " +
              "it in Studio first if you want it built fresh.",
          },
          { kind: "h3", text: "A stage is marked with a red exclamation" },
          {
            kind: "p",
            text:
              "That is the stage the run stopped at. Press View result to see " +
              "what went wrong - the reason is reported there rather than " +
              "against the stage itself.",
          },
          {
            kind: "p",
            text:
              "Everything before it did happen. The flows and the application " +
              "created up to that point are real, and are safe to open in " +
              "Studio.",
          },
        ],
      },
    ],
  },
];

/** Every page in reading order, for Previous and Next. */
export const GUIDE: GuidePage[] = SECTIONS.flatMap((section) => section.pages);

/** The section a page belongs to, for the breadcrumb. */
export function sectionOf(pageId: string): GuideSection | undefined {
  return SECTIONS.find((s) => s.pages.some((p) => p.id === pageId));
}
